/**
 * Entity Templates - Pre-defined templates for common entity types
 */

import type { EntityType, EntityStatus } from './types';

export interface EntityTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'technology' | 'organization' | 'process' | 'governance';
  entityType: EntityType;
  defaultStatus: EntityStatus;
  suggestedTags: string[];
  defaultDescription?: string;
  metadataFields: MetadataFieldTemplate[];
}

export interface MetadataFieldTemplate {
  key: string;
  label: string;
  type: 'text' | 'date' | 'select' | 'url' | 'number';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  defaultValue?: string;
}

export const ENTITY_TEMPLATES: EntityTemplate[] = [
  // Technology Templates
  {
    id: 'web-application',
    name: 'Web Application',
    description: 'Frontend or full-stack web application',
    icon: '🌐',
    category: 'technology',
    entityType: 'application',
    defaultStatus: 'active',
    suggestedTags: ['web', 'frontend', 'application'],
    defaultDescription: 'Web application serving users via browser',
    metadataFields: [
      { key: 'tech_stack', label: 'Tech Stack', type: 'text', placeholder: 'e.g., React, Node.js, PostgreSQL' },
      { key: 'hosting_platform', label: 'Hosting Platform', type: 'select', options: ['AWS', 'Azure', 'GCP', 'Cloudflare', 'Vercel', 'Other'] },
      { key: 'repository_url', label: 'Repository URL', type: 'url', placeholder: 'https://github.com/...' },
      { key: 'documentation_url', label: 'Documentation URL', type: 'url' },
      { key: 'owner', label: 'Owner/Team', type: 'text' },
    ],
  },
  {
    id: 'api-service',
    name: 'API Service',
    description: 'Backend API or microservice',
    icon: '🔌',
    category: 'technology',
    entityType: 'api',
    defaultStatus: 'active',
    suggestedTags: ['api', 'backend', 'service'],
    defaultDescription: 'API service providing data and functionality',
    metadataFields: [
      { key: 'api_type', label: 'API Type', type: 'select', options: ['REST', 'GraphQL', 'gRPC', 'WebSocket', 'Other'] },
      { key: 'programming_language', label: 'Language', type: 'text', placeholder: 'e.g., TypeScript, Go, Python' },
      { key: 'documentation_url', label: 'API Docs URL', type: 'url' },
      { key: 'sla_uptime', label: 'SLA Uptime %', type: 'number', placeholder: '99.9' },
      { key: 'owner', label: 'Owner/Team', type: 'text' },
    ],
  },
  {
    id: 'database',
    name: 'Database',
    description: 'Database or data store',
    icon: '🗄️',
    category: 'technology',
    entityType: 'data_store',
    defaultStatus: 'active',
    suggestedTags: ['database', 'data', 'storage'],
    defaultDescription: 'Database for storing and managing data',
    metadataFields: [
      { key: 'database_type', label: 'Database Type', type: 'select', options: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'Elasticsearch', 'DynamoDB', 'Other'] },
      { key: 'hosting', label: 'Hosting', type: 'select', options: ['Self-hosted', 'AWS RDS', 'Azure SQL', 'GCP Cloud SQL', 'Managed Service', 'Other'] },
      { key: 'backup_frequency', label: 'Backup Frequency', type: 'select', options: ['Continuous', 'Hourly', 'Daily', 'Weekly'] },
      { key: 'data_classification', label: 'Data Classification', type: 'select', options: ['Public', 'Internal', 'Confidential', 'Restricted'] },
      { key: 'size_gb', label: 'Size (GB)', type: 'number' },
    ],
  },
  {
    id: 'third-party-tool',
    name: 'Third-Party Tool',
    description: 'External SaaS or vendor tool',
    icon: '🔧',
    category: 'technology',
    entityType: 'vendor',
    defaultStatus: 'active',
    suggestedTags: ['saas', 'vendor', 'tool'],
    defaultDescription: 'Third-party tool or service',
    metadataFields: [
      { key: 'vendor', label: 'Vendor', type: 'text', required: true },
      { key: 'contract_end_date', label: 'Contract End Date', type: 'date' },
      { key: 'renewal_date', label: 'Renewal Date', type: 'date' },
      { key: 'monthly_cost', label: 'Monthly Cost ($)', type: 'number' },
      { key: 'admin_url', label: 'Admin URL', type: 'url' },
      { key: 'support_contact', label: 'Support Contact', type: 'text' },
    ],
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure Component',
    description: 'Infrastructure or platform component',
    icon: '🏗️',
    category: 'technology',
    entityType: 'infrastructure',
    defaultStatus: 'active',
    suggestedTags: ['infrastructure', 'platform', 'devops'],
    defaultDescription: 'Infrastructure component supporting applications',
    metadataFields: [
      { key: 'platform', label: 'Platform', type: 'select', options: ['AWS', 'Azure', 'GCP', 'On-premise', 'Hybrid', 'Other'] },
      { key: 'component_type', label: 'Component Type', type: 'select', options: ['Compute', 'Network', 'Storage', 'Security', 'Monitoring', 'CI/CD'] },
      { key: 'managed_by', label: 'Managed By', type: 'select', options: ['Platform Team', 'DevOps', 'Cloud Provider', 'Vendor'] },
      { key: 'cost_center', label: 'Cost Center', type: 'text' },
    ],
  },

  // Organization Templates
  {
    id: 'engineering-team',
    name: 'Engineering Team',
    description: 'Development or engineering team',
    icon: '👥',
    category: 'organization',
    entityType: 'team',
    defaultStatus: 'active',
    suggestedTags: ['team', 'engineering', 'development'],
    defaultDescription: 'Engineering team responsible for product development',
    metadataFields: [
      { key: 'team_lead', label: 'Team Lead', type: 'text' },
      { key: 'team_size', label: 'Team Size', type: 'number' },
      { key: 'focus_area', label: 'Focus Area', type: 'text', placeholder: 'e.g., Backend, Frontend, Platform' },
      { key: 'slack_channel', label: 'Slack Channel', type: 'text' },
      { key: 'on_call_rotation', label: 'On-Call Rotation', type: 'select', options: ['Yes', 'No'] },
    ],
  },
  {
    id: 'vendor',
    name: 'Vendor / Partner',
    description: 'External vendor or partner organization',
    icon: '🤝',
    category: 'organization',
    entityType: 'vendor',
    defaultStatus: 'active',
    suggestedTags: ['vendor', 'partner', 'external'],
    defaultDescription: 'External vendor or partner relationship',
    metadataFields: [
      { key: 'contact_name', label: 'Primary Contact', type: 'text' },
      { key: 'contact_email', label: 'Contact Email', type: 'text' },
      { key: 'contract_end_date', label: 'Contract End Date', type: 'date' },
      { key: 'vendor_type', label: 'Vendor Type', type: 'select', options: ['Software', 'Hardware', 'Services', 'Consulting', 'Managed Services'] },
      { key: 'criticality', label: 'Criticality', type: 'select', options: ['Critical', 'High', 'Medium', 'Low'] },
    ],
  },

  // Process Templates
  {
    id: 'business-process',
    name: 'Business Process',
    description: 'Core business process or workflow',
    icon: '⚙️',
    category: 'process',
    entityType: 'process',
    defaultStatus: 'active',
    suggestedTags: ['process', 'workflow', 'business'],
    defaultDescription: 'Business process supporting operations',
    metadataFields: [
      { key: 'process_owner', label: 'Process Owner', type: 'text' },
      { key: 'frequency', label: 'Frequency', type: 'select', options: ['Continuous', 'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Event-driven'] },
      { key: 'automation_level', label: 'Automation Level', type: 'select', options: ['Fully Automated', 'Partially Automated', 'Manual'] },
      { key: 'documentation_url', label: 'Documentation URL', type: 'url' },
    ],
  },
  {
    id: 'capability',
    name: 'Business Capability',
    description: 'Business capability or function',
    icon: '🎯',
    category: 'process',
    entityType: 'capability',
    defaultStatus: 'active',
    suggestedTags: ['capability', 'business', 'function'],
    defaultDescription: 'Business capability enabling organizational outcomes',
    metadataFields: [
      { key: 'capability_level', label: 'Capability Level', type: 'select', options: ['Strategic', 'Core', 'Supporting'] },
      { key: 'maturity', label: 'Maturity', type: 'select', options: ['Initial', 'Developing', 'Defined', 'Managed', 'Optimizing'] },
      { key: 'business_owner', label: 'Business Owner', type: 'text' },
    ],
  },

  // Governance Templates
  {
    id: 'policy',
    name: 'Policy / Standard',
    description: 'Policy, standard, or guideline',
    icon: '📜',
    category: 'governance',
    entityType: 'policy',
    defaultStatus: 'active',
    suggestedTags: ['policy', 'governance', 'compliance'],
    defaultDescription: 'Policy or standard governing organizational practices',
    metadataFields: [
      { key: 'policy_type', label: 'Policy Type', type: 'select', options: ['Security', 'Privacy', 'Compliance', 'Operational', 'HR', 'Technical'] },
      { key: 'approval_date', label: 'Approval Date', type: 'date' },
      { key: 'review_date', label: 'Next Review Date', type: 'date' },
      { key: 'policy_owner', label: 'Policy Owner', type: 'text' },
      { key: 'document_url', label: 'Document URL', type: 'url' },
    ],
  },
  {
    id: 'compliance-requirement',
    name: 'Compliance Requirement',
    description: 'Regulatory or compliance requirement',
    icon: '🛡️',
    category: 'governance',
    entityType: 'compliance_framework',
    defaultStatus: 'active',
    suggestedTags: ['compliance', 'regulation', 'standard'],
    defaultDescription: 'Compliance requirement or regulatory standard',
    metadataFields: [
      { key: 'framework', label: 'Framework', type: 'select', options: ['SOC 2', 'ISO 27001', 'GDPR', 'HIPAA', 'PCI-DSS', 'FedRAMP', 'Other'] },
      { key: 'compliance_status', label: 'Compliance Status', type: 'select', options: ['Compliant', 'In Progress', 'Non-Compliant', 'Not Applicable'] },
      { key: 'next_audit_date', label: 'Next Audit Date', type: 'date' },
      { key: 'certifying_body', label: 'Certifying Body', type: 'text' },
      { key: 'certificate_expiry', label: 'Certificate Expiry', type: 'date' },
    ],
  },
  {
    id: 'risk',
    name: 'Risk',
    description: 'Identified risk or threat',
    icon: '⚠️',
    category: 'governance',
    entityType: 'risk',
    defaultStatus: 'active',
    suggestedTags: ['risk', 'threat', 'governance'],
    defaultDescription: 'Identified risk requiring monitoring and mitigation',
    metadataFields: [
      { key: 'risk_category', label: 'Risk Category', type: 'select', options: ['Security', 'Operational', 'Financial', 'Compliance', 'Strategic', 'Technical'] },
      { key: 'likelihood', label: 'Likelihood', type: 'select', options: ['Very High', 'High', 'Medium', 'Low', 'Very Low'] },
      { key: 'impact', label: 'Impact', type: 'select', options: ['Critical', 'High', 'Medium', 'Low', 'Minimal'] },
      { key: 'mitigation_status', label: 'Mitigation Status', type: 'select', options: ['Mitigated', 'In Progress', 'Accepted', 'Not Started'] },
      { key: 'risk_owner', label: 'Risk Owner', type: 'text' },
    ],
  },
];

// Template categories for grouping in UI
export const TEMPLATE_CATEGORIES = {
  technology: { label: 'Technology', icon: '💻' },
  organization: { label: 'Organization', icon: '🏢' },
  process: { label: 'Process', icon: '⚙️' },
  governance: { label: 'Governance', icon: '📋' },
};

// Get templates by category
export function getTemplatesByCategory(category: keyof typeof TEMPLATE_CATEGORIES): EntityTemplate[] {
  return ENTITY_TEMPLATES.filter(t => t.category === category);
}

// Get template by ID
export function getTemplateById(id: string): EntityTemplate | undefined {
  return ENTITY_TEMPLATES.find(t => t.id === id);
}
